import { useSelector } from 'react-redux';

import { isPast, secondsToMilliseconds } from 'date-fns';

import { selectLayoutConfig } from 'store/config/selectors';
import {
    selectCatchupIconValid,
    selectChannelById,
    selectChannelsById,
} from 'store/programs/selectors';
import { MixedItem } from 'store/railsBuilder/reducer.types';
import { ReduxStore } from 'store/types';
import {
    selectCatchupCapability,
    selectEntitlements,
    selectStartoverCapability,
} from 'store/user/selectors';
import { isCapabilityValid } from 'store/utils';

import useDeeplinkItem from 'utils/hooks/useDeeplinkItem';
import useIsLive from 'utils/hooks/useIsLive';
import useMetadata from 'utils/hooks/useMetadata';
import useMixedItemParentalRating from 'utils/hooks/useMixedItemParentalRating';
import useSeriesMetadata from 'utils/hooks/useSeriesMetadata';
import isSubscribedToChannel from 'utils/isSubscribedToChannel';

import useGalleryOnPress from 'components/Gallery/hooks/useGalleryOnPress';
import { ImageSize } from 'components/NagraImage/types';
import { OnUavAnalyticsProps, StandardUavProps } from 'components/Ribbon/types';

type Props = {
    item: MixedItem;
    index: number;
    onPress(item: MixedItem, index: number): void;
} & StandardUavProps &
    OnUavAnalyticsProps;

const useGallery = ({
    item,
    onPress,
    onThirdPartyAppStart,
    onRailsSelection,
    onDeepLinkTriggered,
    railId,
    templateId,
    index,
}: Props) => {
    const {
        id,
        title,
        contentType,
        startTime,
        endTime,
        channelId,
        CUEndDate,
        isCatchUp,
        isStartOver,
    } = item;
    const { width } = ImageSize.BACKGROUND;

    const channel = useSelector((state: ReduxStore) =>
        selectChannelById(state, item.channelId),
    );
    const { primary } = useSelector(selectLayoutConfig);
    const entitlements = useSelector(selectEntitlements);
    const catchupCapability = useSelector(selectCatchupCapability);
    const startoverCapability = useSelector(selectStartoverCapability);
    const channelsById = useSelector(selectChannelsById);
    const metadata = useMetadata(item);
    const seriesMetadata = useSeriesMetadata(item);

    const CUEndDateValid = useSelector((state: ReduxStore) =>
        selectCatchupIconValid(state, endTime, CUEndDate),
    );

    const parentalRating = useMixedItemParentalRating(item);

    const isLive = useIsLive({
        startTime,
        endTime,
    });

    const ableToPlayLive = !!(isLive && channelId && channelsById[channelId]);

    const isChannelSubscribed =
        channel && isSubscribedToChannel(channel, entitlements);
    const shouldDisplaySubscribeIcon =
        isChannelSubscribed !== undefined && !isChannelSubscribed;

    const isCatchupAsset = !!(
        isCatchUp &&
        CUEndDateValid &&
        startTime &&
        endTime &&
        isPast(secondsToMilliseconds(endTime))
    );
    const ableToPlayCatchup =
        isCatchupAsset && isCapabilityValid(catchupCapability);

    const ableToStartOver = !!(
        isCapabilityValid(startoverCapability) &&
        ableToPlayLive &&
        isStartOver
    );

    const { onPressPossibleDeeplinkItem, isValidDeeplinkItem } =
        useDeeplinkItem({
            item,
            ableToPlayCatchup,
            onDeepLinkTriggered,
            onThirdPartyAppStart,
            onRailsSelection,
            railId,
            templateId,
            playbackType: item.playbackType,
            index,
        });

    const handlePress = useGalleryOnPress({
        item,
        index,
        onPressPossibleDeeplinkItem,
        onPress,
    });

    return {
        id,
        title,
        channelId,
        contentType,
        width,
        primary,
        metadata,
        isCatchupAsset,
        ableToPlayCatchup,
        ableToStartOver,
        seriesMetadata,
        parentalRating,
        handlePress,
        isValidDeeplinkItem,
        shouldDisplaySubscribeIcon,
    };
};

export default useGallery;
